/**
 * Form primitives shared by every screen that edits something.
 *
 * These started life in `settings/SettingsKit.web.jsx` when settings was the
 * only editing surface. Step 3 made contacts editable too, so they moved here
 * and SettingsKit re-exports them — one definition, and the settings screens
 * did not need touching.
 *
 * They live in the app rather than the design system because they encode
 * MeshHub's form conventions (RTL dialogs, server-error surfacing, Jalali date
 * entry), not generic visual primitives. `Button`, `Card`, `Spinner` and the
 * rest still come from `@meshhub/design-tokens/ui`.
 */
import * as Dialog from '@radix-ui/react-dialog';
import { Button, EmptyState, Spinner } from '@meshhub/design-tokens/ui';
import { CalendarDays, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';
import {
  PERSIAN_WEEKDAY_INITIALS,
  addJalaliMonths,
  formatGregorian,
  formatPhone,
  fromPersianDigits,
  isoToJalaliDate,
  jalaliMonthGrid,
  jalaliMonthTitle,
  jalaliToIsoDate,
  toPersianDigits,
  todayIsoDate,
} from '../lib/format.js';
import { S } from '../lib/strings.js';

const FIELD_BASE =
  'h-control rounded border border-border bg-bg text-base outline-none ' +
  'focus:border-primary disabled:opacity-60';

const FIELD_CLASS = `${FIELD_BASE} px-4`;

/**
 * The same field, with room for an icon button inside it.
 *
 * The arithmetic is why this is a separate class rather than an extra utility
 * appended to the one above. The adornment starts at `space-2` and is `w-6`
 * wide, so it ends at `space-2 + space-6` — while `px-4` starts the text at
 * `space-4`, which is well inside that. The value was drawn straight through
 * the icon, which is exactly what a user reported. `space-7` is the first step
 * on the scale that clears it, and every term here is a token, so a deployment
 * that rescales its spacing keeps the relationship rather than reopening the
 * bug.
 *
 * Composed rather than layered (`px-4 pl-7`) so nothing depends on the order
 * Tailwind happens to emit `px-*` and `pl-*` in.
 */
const FIELD_CLASS_ADORNED = `${FIELD_BASE} pl-7 pr-4`;

/**
 * Label, hint and error for one control, wired by id rather than by wrapping.
 *
 * A wrapping `<label>` would make the hint part of the control's accessible
 * name — a screen reader would announce «یادداشت اولین تماس یادداشت به‌عنوان
 * اولین تعامل ثبت می‌شود» as the field's name. `htmlFor` plus
 * `aria-describedby` keeps the name short and the guidance separate, which is
 * what both a screen reader and a test expect.
 */
function useFieldIds(hasHint, hasError) {
  const id = React.useId();
  return {
    id,
    describedBy:
      [hasHint && `${id}-hint`, hasError && `${id}-error`].filter(Boolean).join(' ') ||
      undefined,
    hintId: `${id}-hint`,
    errorId: `${id}-error`,
  };
}

function FieldFrame({ label, hint, error, ids, className, children }) {
  return (
    <div className={`flex flex-col gap-1 ${className ?? ''}`}>
      {label && (
        <label htmlFor={ids.id} className="text-sm text-text-muted">
          {label}
        </label>
      )}
      {children}
      {hint && !error && (
        <span id={ids.hintId} className="text-xs text-text-muted">
          {hint}
        </span>
      )}
      {error && (
        <span id={ids.errorId} role="alert" className="text-xs text-danger">
          {error}
        </span>
      )}
    </div>
  );
}

/** Every deactivating or destructive action confirms before it happens. */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  message,
  confirmLabel,
  onConfirm,
  danger,
  children,
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-text/30" />
        <Dialog.Content
          dir="rtl"
          className="fixed left-1/2 top-1/2 w-overlay -translate-x-1/2 -translate-y-1/2 rounded
                     border border-border bg-surface p-6 shadow-overlay"
        >
          <Dialog.Title className="text-md font-bold">{title}</Dialog.Title>
          <Dialog.Description className="mt-3 text-base text-text-muted">
            {message}
          </Dialog.Description>
          {children && <div className="mt-4">{children}</div>}
          <div className="mt-6 flex justify-start gap-3">
            <Button
              variant={danger ? 'danger' : 'primary'}
              onClick={() => {
                onConfirm();
                onOpenChange(false);
              }}
            >
              {confirmLabel ?? S.app.confirm}
            </Button>
            <Button onClick={() => onOpenChange(false)}>{S.app.cancel}</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * A modal form.
 *
 * `wide` picks the second of the design system's two overlay widths — a
 * composer with a dropzone and a long textarea needs more room than a confirm
 * dialog, and two named widths beat a per-screen guess.
 */
/**
 * How a field's own popover tells the dialog around it "Esc is mine".
 *
 * WHY A CONTEXT AND NOT A KEY HANDLER. Radix's dialog listens for Escape on
 * `document` in the CAPTURE phase, so nothing inside the dialog — a React
 * handler on a wrapper, a native listener on the popover — can ever run first
 * and stop it. The only two places that could are a `window` capture listener,
 * which the doctrine reserves for `lib/shortcuts.jsx` and `npm run lint`
 * enforces, and Radix's own `onEscapeKeyDown` hook. This is that hook.
 *
 * A COUNT rather than a boolean: two fields on one form can each have a
 * popover, and a boolean would be cleared by whichever closed first.
 *
 * The popover still closes itself — Radix's `preventDefault` stops the
 * dismiss, not the propagation, so the event still bubbles to the field's own
 * handler. What this stops is Escape throwing away a half-filled form because
 * somebody wanted to dismiss a calendar.
 */
const PopoverGuard = React.createContext(null);

/** Hold the guard while `open`. Returns nothing; the effect is the point. */
function useEscapeGuard(open) {
  const guard = React.useContext(PopoverGuard);
  React.useEffect(() => {
    if (!open || !guard) return undefined;
    guard.current += 1;
    return () => {
      guard.current -= 1;
    };
  }, [open, guard]);
}

/**
 * @param canSubmit  false disables the submit button. Distinct from `busy`,
 *   which means "a request is in flight": this one means "this form cannot be
 *   submitted at all right now". `SendMessageDialog` has passed it since Step
 *   5 and this component silently ignored it — so «ارسال پیام» was clickable
 *   with no template chosen, and the resulting error was dropped too, because
 *   `error` was ignored in exactly the same way. Both were invisible: the
 *   mutation failed and the dialog just sat there.
 * @param error  rendered above the form, so a refusal lands where the person
 *   who caused it is looking.
 */
export function FormDialog({
  open,
  onOpenChange,
  title,
  hint,
  onSubmit,
  submitLabel,
  busy,
  canSubmit = true,
  error,
  wide,
  footer,
  children,
}) {
  // How many field popovers inside this dialog are open right now.
  const openPopovers = React.useRef(0);
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-text/30" />
        <Dialog.Content
          dir="rtl"
          // The innermost overlay wins. With a date grid or a time list open,
          // Escape dismisses THAT and the form survives.
          onEscapeKeyDown={(event) => {
            if (openPopovers.current > 0) event.preventDefault();
          }}
          className={`fixed left-1/2 top-1/2 max-h-[85vh] -translate-x-1/2 -translate-y-1/2
                      overflow-auto rounded border border-border bg-surface p-6 shadow-overlay
                      ${wide ? 'w-overlay-wide' : 'w-overlay'}`}
        >
          <PopoverGuard.Provider value={openPopovers}>
          <Dialog.Title className="text-md font-bold">{title}</Dialog.Title>
          {hint ? (
            <Dialog.Description className="mt-1 text-sm text-text-muted">{hint}</Dialog.Description>
          ) : (
            // Radix warns when a dialog has no description; an empty one is
            // the documented way to say "deliberately none".
            <Dialog.Description className="sr-only">{title}</Dialog.Description>
          )}
          <form
            className="mt-5 flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit();
            }}
          >
            <MutationError error={error} />
            {children}
            <div className="mt-2 flex items-center justify-start gap-3">
              <Button type="submit" variant="primary" disabled={busy || !canSubmit}>
                {busy ? <Spinner /> : (submitLabel ?? S.app.save)}
              </Button>
              {footer}
              <Button type="button" onClick={() => onOpenChange(false)}>
                {S.app.cancel}
              </Button>
            </div>
          </form>
          </PopoverGuard.Provider>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Ref-forwarding, so a caller can return focus to it — quick-add's
 *  "save and add another" puts the cursor back on the surname. */
/**
 * @param adornment  a control rendered INSIDE the box, at its left edge — the
 *   date and time fields' popover triggers. It is positioned against the input
 *   alone rather than against the field, which is the bug this slot exists to
 *   make impossible: an absolutely-positioned `bottom-1` on the field wrapper
 *   sat on the input while there was no hint, and slid down onto the hint or
 *   the validation error the moment there was one.
 */
export const TextField = React.forwardRef(function TextField(
  { label, hint, error, className, adornment, ...props },
  ref,
) {
  const ids = useFieldIds(Boolean(hint), Boolean(error));
  const input = (
    <input
      ref={ref}
      id={ids.id}
      aria-describedby={ids.describedBy}
      aria-invalid={error ? true : undefined}
      className={adornment ? `${FIELD_CLASS_ADORNED} w-full` : FIELD_CLASS}
      {...props}
    />
  );
  return (
    <FieldFrame label={label} hint={hint} error={error} ids={ids} className={className}>
      {adornment ? (
        // PHYSICAL left, not `start`: the fields that use this render their
        // value `dir="ltr"` inside an RTL page, so the text begins at the left
        // edge and that is the edge the icon has to keep clear of.
        <div className="relative flex">
          {input}
          <span className="absolute inset-y-0 left-2 flex items-center">{adornment}</span>
        </div>
      ) : (
        input
      )}
    </FieldFrame>
  );
});

// A ref reaches the <textarea> itself, like TextField's does, so a caller can
// insert at the CARET rather than only append — the send dialog's placeholder
// chips need to know where the cursor is.
export const TextArea = React.forwardRef(function TextArea(
  { label, hint, error, rows = 4, className, ...props },
  ref,
) {
  const ids = useFieldIds(Boolean(hint), Boolean(error));
  return (
    <FieldFrame label={label} hint={hint} error={error} ids={ids} className={className}>
      <textarea
        ref={ref}
        id={ids.id}
        rows={rows}
        aria-describedby={ids.describedBy}
        aria-invalid={error ? true : undefined}
        className="rounded border border-border bg-bg px-4 py-2 text-base outline-none
                   focus:border-primary disabled:opacity-60"
        {...props}
      />
    </FieldFrame>
  );
});

export function SelectField({ label, hint, error, className, children, ...props }) {
  const ids = useFieldIds(Boolean(hint), Boolean(error));
  return (
    <FieldFrame label={label} hint={hint} error={error} ids={ids} className={className}>
      <select
        id={ids.id}
        aria-describedby={ids.describedBy}
        aria-invalid={error ? true : undefined}
        className={FIELD_CLASS}
        {...props}
      >
        {children}
      </select>
    </FieldFrame>
  );
}

export function CheckboxField({ label, ...props }) {
  return (
    <label className="flex items-center gap-2 text-base">
      <input type="checkbox" className="accent-primary" {...props} />
      {label}
    </label>
  );
}

/**
 * A Jalali date field: a CALENDAR you pick from, and a box you may still type
 * into.
 *
 * It used to be typing only, because `<input type="date">` shows the browser's
 * calendar and every browser's is Gregorian — there is no attribute that makes
 * it Shamsi. That was the right call about the NATIVE control and the wrong
 * answer to the question: a rep booking «سه‌شنبه هفته بعد» does not know the
 * number, and typing «۱۴۰۵/۰۶/۱۰» to find out is arithmetic the app should be
 * doing.
 *
 * So the typed box stays — it is faster once you know the date, it is
 * keyboard-only, and removing it would take a working path away — and a month
 * grid opens beside it.
 *
 * **The grid does no calendar arithmetic of its own.** `jalaliMonthGrid` and
 * `addJalaliMonths` live in `lib/format.js`, the one module allowed to make
 * locale decisions (CLAUDE.md §12). A picker with its own month maths would be
 * a second calendar, and the two would disagree about Esfand 30 in a leap year
 * long before anybody noticed.
 *
 * The value handed to `onChange` is always an ISO Gregorian date, exactly as
 * before, so no caller has to change and none of them touches a calendar.
 */
export function JalaliDateField({
  label,
  value,
  onChange,
  hint,
  error,
  className,
  ...props
}) {
  const [text, setText] = React.useState(() => isoToJalaliDate(value));
  const [open, setOpen] = React.useState(false);
  // Which month the grid is showing. Follows the value while there is one, so
  // reopening the picker lands where the date is rather than on today.
  const [month, setMonth] = React.useState(() => value || todayIsoDate());
  const lastEmitted = React.useRef(value);
  const boxRef = React.useRef(null);
  useEscapeGuard(open);

  // Re-sync when the value changes from outside (a form reset, a fresh
  // record) — but not on our own emissions, which would fight the typist.
  React.useEffect(() => {
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      setText(isoToJalaliDate(value));
      if (value) setMonth(value);
    }
  }, [value]);

  // A click anywhere else closes the grid. `mousedown` rather than `click`, so
  // the grid is gone before the thing underneath it reacts.
  React.useEffect(() => {
    if (!open) return undefined;
    const away = (event) => {
      if (!boxRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const emit = (iso) => {
    lastEmitted.current = iso;
    setText(isoToJalaliDate(iso));
    onChange(iso);
  };

  const handleTyping = (raw) => {
    setText(raw);
    const iso = jalaliToIsoDate(raw);
    // An empty box clears the date; a half-typed one emits nothing, so the
    // form does not see «۱۴۰» as a date.
    if (raw.trim() === '' || iso) {
      lastEmitted.current = iso;
      if (iso) setMonth(iso);
      onChange(iso);
    }
  };

  const invalid = text.trim() !== '' && jalaliToIsoDate(text) === null;

  return (
    // Esc closes the grid without closing the DIALOG this field sits in — the
    // innermost overlay wins, which is the rule the shortcut registry already
    // keeps for dialogs over screens.
    //
    // The handler is on the WRAPPER, not on the input. Once somebody clicks a
    // day the focus is on that BUTTON, so an input-only handler never saw the
    // key and Radix closed the whole activity dialog underneath — losing a
    // half-filled form to the key people press to dismiss a popover.
    <div
      ref={boxRef}
      className={`relative flex flex-col ${className ?? ''}`}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.stopPropagation();
          setOpen(false);
        }
      }}
    >
      <TextField
        label={label}
        value={text}
        onChange={(event) => handleTyping(event.target.value)}
        onFocus={() => setOpen(true)}
        placeholder={toPersianDigits('1405/01/01')}
        inputMode="numeric"
        dir="ltr"
        autoComplete="off"
        hint={hint}
        error={error ?? (invalid ? S.interaction.invalidDate : null)}
        adornment={
          <button
            type="button"
            className="flex h-6 w-6 items-center justify-center rounded
                       text-text-muted hover:text-text"
            aria-label={S.app.pickDate}
            aria-expanded={open}
            title={S.app.pickDate}
            onClick={() => setOpen((v) => !v)}
          >
            <CalendarDays size={15} aria-hidden="true" />
          </button>
        }
        {...props}
      />

      {open && (
        <JalaliMonthGrid
          month={month}
          selected={value}
          onMonth={setMonth}
          onPick={(iso) => {
            emit(iso);
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * A button that opens the same Jalali month grid and reports the day picked.
 *
 * The field above owns a text box; this owns whatever the caller renders — the
 * calendar hangs it on its own range title, because a title you can click to
 * jump is the affordance every calendar has and it costs no toolbar width.
 *
 * The grid, the month arithmetic, the click-away and the Esc rule are the
 * field's; only the trigger differs. A second month grid built beside this one
 * would disagree about Esfand 30 in a leap year long before anybody noticed
 * (CLAUDE.md §12).
 */
export function JalaliDateButton({ value, onPick, label, className, children }) {
  const [open, setOpen] = React.useState(false);
  const [month, setMonth] = React.useState(() => value || todayIsoDate());
  const boxRef = React.useRef(null);
  useEscapeGuard(open);

  // Follow the caller: stepping the calendar a week at a time and then opening
  // the grid must land on the week being looked at, not on the month it was
  // last opened in.
  React.useEffect(() => {
    if (value) setMonth(value);
  }, [value]);

  React.useEffect(() => {
    if (!open) return undefined;
    const away = (event) => {
      if (!boxRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  return (
    <div
      ref={boxRef}
      className={`relative ${className ?? ''}`}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.stopPropagation();
          setOpen(false);
        }
      }}
    >
      <button
        type="button"
        className="flex items-center gap-2 rounded px-2 py-1 hover:bg-surface-hover"
        aria-label={label}
        aria-expanded={open}
        title={label}
        onClick={() => setOpen((v) => !v)}
      >
        {children}
        <CalendarDays size={14} className="text-text-muted" aria-hidden="true" />
      </button>

      {open && (
        <JalaliMonthGrid
          month={month}
          selected={value}
          onMonth={setMonth}
          onPick={(iso) => {
            onPick(iso);
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}

/** The popover itself. Split out only so `JalaliDateField` stays readable. */
function JalaliMonthGrid({ month, selected, onMonth, onPick }) {
  const weeks = jalaliMonthGrid(month);
  return (
    <div
      role="dialog"
      aria-label={S.app.pickDate}
      className="absolute top-full z-20 mt-1 w-64 rounded border border-border bg-surface
                 p-3 shadow-overlay"
    >
      <div className="mb-2 flex items-center justify-between">
        {/* In RTL the «previous» chevron points RIGHT. `ChevronRight` is the
            earlier month for the same reason the week starts on the right. */}
        <button
          type="button"
          aria-label={S.app.nextMonth}
          className="rounded px-1 text-text-muted hover:text-text"
          onClick={() => onMonth(addJalaliMonths(month, 1))}
        >
          <ChevronLeft size={16} aria-hidden="true" />
        </button>
        <span className="text-sm font-medium">{jalaliMonthTitle(month)}</span>
        <button
          type="button"
          aria-label={S.app.previousMonth}
          className="rounded px-1 text-text-muted hover:text-text"
          onClick={() => onMonth(addJalaliMonths(month, -1))}
        >
          <ChevronRight size={16} aria-hidden="true" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5 text-center">
        {PERSIAN_WEEKDAY_INITIALS.map((initial, index) => (
          <span
            key={initial + index}
            className="py-1 text-xs text-text-muted"
            aria-hidden="true"
          >
            {initial}
          </span>
        ))}
        {weeks.flat().map((cell) => {
          const isSelected = cell.iso === selected;
          return (
            <button
              key={cell.iso}
              type="button"
              onClick={() => onPick(cell.iso)}
              aria-current={cell.isToday ? 'date' : undefined}
              aria-pressed={isSelected}
              // The GREGORIAN date on hover — the same pairing the calendar
              // header shows, for the same reason.
              title={`${toPersianDigits(isoToJalaliDate(cell.iso))} — ${formatGregorian(cell.iso)}`}
              className={[
                'rounded py-1 text-sm',
                isSelected
                  ? 'bg-primary text-text-inverse'
                  : cell.inMonth
                    ? 'text-text hover:bg-surface-hover'
                    : 'text-text-muted hover:bg-surface-hover',
                cell.isToday && !isSelected ? 'border border-primary' : '',
                cell.isWeekend && !isSelected ? 'text-danger' : '',
              ].join(' ')}
            >
              {cell.label}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        className="mt-2 w-full rounded py-1 text-xs text-primary hover:bg-surface-hover"
        onClick={() => onPick(todayIsoDate())}
      >
        {S.app.today}
      </button>
    </div>
  );
}

/**
 * A time field: a list you pick from, and a box you may still type into.
 *
 * Same shape and same reasoning as the date field above. `<input type="time">`
 * was not an option either: whether it shows a 24-hour or an AM/PM face is the
 * BROWSER's locale, so what a user sees would depend on their machine rather
 * than on the app — the exact failure ui.md already records for the filter
 * panel's date inputs.
 *
 * The stored value is `HH:mm` in ASCII, unchanged, because that is what
 * `isoDateAndTimeToInstant` consumes and this task was asked not to add a
 * second source of truth for date and time.
 */
export function TimeField({
  label,
  value,
  onChange,
  hint,
  error,
  className,
  minuteStep = 5,
  ...props
}) {
  const [open, setOpen] = React.useState(false);
  const boxRef = React.useRef(null);
  useEscapeGuard(open);

  React.useEffect(() => {
    if (!open) return undefined;
    const away = (event) => {
      if (!boxRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  // Persian digits are accepted and folded at the edge, exactly as they are in
  // the phone and date fields — a Persian keyboard is what is already open.
  const handleTyping = (raw) => onChange(fromPersianDigits(raw));

  const [hour, minute] = /^(\d{1,2}):(\d{2})$/.exec(value ?? '')?.slice(1) ?? [];
  const setPart = (h, m) => onChange(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: Math.ceil(60 / minuteStep) }, (_, i) => i * minuteStep);

  return (
    // Same wrapper-level Esc as the date field, for the same reason: after a
    // pick the focus is on a button inside the popover.
    <div
      ref={boxRef}
      className={`relative flex flex-col ${className ?? ''}`}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.stopPropagation();
          setOpen(false);
        }
      }}
    >
      <TextField
        label={label}
        value={value ?? ''}
        onChange={(event) => handleTyping(event.target.value)}
        onFocus={() => setOpen(true)}
        placeholder="09:00"
        inputMode="numeric"
        dir="ltr"
        autoComplete="off"
        hint={hint}
        error={error}
        adornment={
          <button
            type="button"
            className="flex h-6 w-6 items-center justify-center rounded
                       text-text-muted hover:text-text"
            aria-label={S.app.pickTime}
            aria-expanded={open}
            title={S.app.pickTime}
            onClick={() => setOpen((v) => !v)}
          >
            <Clock size={15} aria-hidden="true" />
          </button>
        }
        {...props}
      />

      {open && (
        <div
          role="dialog"
          aria-label={S.app.pickTime}
          className="absolute top-full z-20 mt-1 flex gap-2 rounded border border-border
                     bg-surface p-2 shadow-overlay"
        >
          <TimeColumn
            label={S.app.hour}
            values={hours}
            current={hour === undefined ? null : Number(hour)}
            onPick={(h) => setPart(h, minute ?? 0)}
          />
          <TimeColumn
            label={S.app.minute}
            values={minutes}
            current={minute === undefined ? null : Number(minute)}
            onPick={(m) => setPart(hour ?? 9, m)}
          />
        </div>
      )}
    </div>
  );
}

/** One scrolling column of the time picker. */
function TimeColumn({ label, values, current, onPick }) {
  return (
    <div className="flex flex-col">
      <span className="pb-1 text-center text-xs text-text-muted">{label}</span>
      <div className="h-40 w-14 overflow-y-auto">
        {values.map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => onPick(v)}
            className={`block w-full rounded py-1 text-center text-sm tabular-nums ${
              v === current
                ? 'bg-primary text-text-inverse'
                : 'text-text hover:bg-surface-hover'
            }`}
          >
            {toPersianDigits(String(v).padStart(2, '0'))}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Surfaces a server-side domain rule as a readable message, not a stack trace. */
export function MutationError({ error }) {
  if (!error) return null;
  const duplicate = error.graphQLErrors?.[0]?.extensions;
  if (duplicate?.code === 'DUPLICATE_PHONE') return <DuplicatePhoneError {...duplicate} />;
  const message = error.graphQLErrors?.[0]?.message ?? error.message ?? S.app.unknownError;
  return (
    <p role="alert" className="rounded border border-danger px-4 py-2 text-base text-danger">
      {message}
    </p>
  );
}

/**
 * The CRM refused a number a live contact already holds. Every form that
 * writes a number renders its errors here — quick-add on desktop and phone,
 * the profile's phones panel — so one branch covers all of them. The link is
 * the point: at a congress the useful answer to «duplicate» is «open the one
 * we already have», not «try again».
 */
function DuplicatePhoneError({ contactId, contactName, number, sameContact }) {
  // An LTR isolate (U+2066 … U+2069), or the «+» of «+۹۸۹۱۲…» lands on the
  // wrong end of the number inside a right-to-left sentence.
  const shown = `\u2066${formatPhone(number)}\u2069`;
  return (
    <p role="alert" className="rounded border border-danger px-4 py-2 text-base text-danger">
      {sameContact
        ? S.contacts.detail.duplicatePhoneSameContact(shown)
        : S.contacts.detail.duplicatePhone(shown, contactName)}
      {!sameContact && contactId && (
        <>
          {' '}
          <Link to={`/contacts/${contactId}`} className="font-medium text-primary underline">
            {S.contacts.detail.openExistingContact}
          </Link>
        </>
      )}
    </p>
  );
}

export function LoadingOrEmpty({ loading, empty, emptyTitle, emptyHint }) {
  if (loading) {
    return (
      <div className="flex justify-center p-7">
        <Spinner />
      </div>
    );
  }
  if (empty) return <EmptyState title={emptyTitle ?? S.app.notFound} hint={emptyHint} />;
  return null;
}
